import { computed, reactive, ref, type ComputedRef } from 'vue'
import { ElMessage, ElMessageBox } from 'element-plus'
import { fenToYuan, yuanToFen } from '@jiaofa/shared-utils'
import { createDeliveryArea, deleteDeliveryArea, deleteDeliveryPoint, deleteDeliveryRule, fetchDeliveryAreas, fetchDeliveryPoints, fetchDeliveryRules, saveDeliveryPoint, updateDeliveryRule, type DeliveryArea, type DeliveryAreaSavePayload, type DeliveryPoint, type DeliveryPointSavePayload, type DeliveryRule, type DeliveryRuleSavePayload } from '@/api/delivery'

type FormatAmount = (amountInFen: number) => string

function fenAmountToYuan(amountInFen?: number): number | undefined {
  return amountInFen === undefined ? undefined : Number(fenToYuan(amountInFen))
}

function yuanAmountToFen(amountInYuan?: number | string | null): number | undefined {
  return amountInYuan === undefined || amountInYuan === null || amountInYuan === '' ? undefined : yuanToFen(amountInYuan)
}

export function useDeliveryCenter(options: {
  isDeliveryRulesCenter: ComputedRef<boolean>
  isDeliveryMinAmountCenter: ComputedRef<boolean>
  isDeliveryAreasCenter: ComputedRef<boolean>
  isDeliveryPointsCenter: ComputedRef<boolean>
  formatAmount: FormatAmount
  ensureEditorOptions: () => Promise<void>
}) {
  const loadingDeliveryRules = ref(false)
  const loadingDeliveryAreas = ref(false)
  const loadingDeliveryPoints = ref(false)
  const deliveryRuleEditorVisible = ref(false)
  const deliveryAreaEditorVisible = ref(false)
  const deliveryPointEditorVisible = ref(false)
  const deliveryPointLocationVisible = ref(false)
  const deliveryRuleSubmitting = ref(false)
  const deliveryAreaSubmitting = ref(false)
  const deliveryPointSubmitting = ref(false)
  const deliveryRules = ref<DeliveryRule[]>([])
  const deliveryAreas = ref<DeliveryArea[]>([])
  const deliveryPoints = ref<DeliveryPoint[]>([])
  const selectedDeliveryPoint = ref<DeliveryPoint>()
  const deliveryRuleStoreIds = ref<string[]>([])
  const deliveryAreaStoreIds = ref<string[]>([])
  const deliveryRuleForm = reactive<DeliveryRuleSavePayload>({
    id: undefined,
    storeId: '',
    name: '',
    minOrderAmount: 0,
    baseFee: 0,
    freeThreshold: 0,
    perKmFee: 0,
    startDistanceKm: 0,
    maxDistanceKm: 0,
  })
  const deliveryAreaForm = reactive<DeliveryAreaSavePayload>({
    storeId: '',
    type: 'RADIUS',
    radiusKm: 3,
    polygon: '',
    feeTier: 0,
    sort: 0,
  })
  const deliveryPointForm = reactive<DeliveryPointSavePayload>({
    id: undefined,
    storeId: '',
    name: '',
    address: '',
    longitude: 0,
    latitude: 0,
    contactName: '',
    contactPhone: '',
    serviceRadiusKm: 1,
    enabled: true,
    sort: 0,
    remark: '',
  })

  const isEditingDeliveryRule = computed(() => Boolean(deliveryRuleForm.id))
  const deliveryRuleStats = computed(() => {
    const totalMinAmount = deliveryRules.value.reduce((sum, item) => sum + (item.minOrderAmount ?? 0), 0)
    return [
      { label: '规则数量', value: String(deliveryRules.value.length), sub: '真实规则列表', tone: 'brand' as const },
      { label: '覆盖门店', value: String(new Set(deliveryRules.value.map((item) => item.storeId).filter(Boolean)).size), sub: '已配置门店数', tone: 'success' as const },
      { label: '平均起送', value: options.formatAmount(deliveryRules.value.length ? Math.round(totalMinAmount / deliveryRules.value.length) : 0), sub: '规则均值', tone: 'warning' as const },
      { label: '免运规则', value: String(deliveryRules.value.filter((item) => (item.freeThreshold ?? 0) > 0).length), sub: '包含免配送费门槛', tone: 'brand' as const },
    ]
  })
  const deliveryMinAmountStats = computed(() => {
    const maxThreshold = Math.max(0, ...deliveryRules.value.map((item) => item.freeThreshold ?? 0))
    return [
      { label: '起送规则', value: String(deliveryRules.value.length), sub: '复用配送规则', tone: 'brand' as const },
      { label: '最低起送', value: options.formatAmount(Math.min(...(deliveryRules.value.length ? deliveryRules.value.map((item) => item.minOrderAmount ?? 0) : [0]))), sub: '当前最小门槛', tone: 'success' as const },
      { label: '最高免运', value: options.formatAmount(maxThreshold), sub: '免配送费门槛', tone: 'warning' as const },
      { label: '全门店规则', value: String(deliveryRules.value.filter((item) => !item.storeId).length), sub: '未绑定门店', tone: 'brand' as const },
    ]
  })
  const deliveryAreaStats = computed(() => {
    return [
      { label: '范围数量', value: String(deliveryAreas.value.length), sub: '真实范围列表', tone: 'brand' as const },
      { label: '半径范围', value: String(deliveryAreas.value.filter((item) => item.type === 'RADIUS').length), sub: '按公里覆盖', tone: 'success' as const },
      { label: '多边形范围', value: String(deliveryAreas.value.filter((item) => item.type === 'POLYGON').length), sub: '按坐标覆盖', tone: 'warning' as const },
      { label: '覆盖门店', value: String(new Set(deliveryAreas.value.map((item) => item.storeId).filter(Boolean)).size), sub: '已配置门店数', tone: 'brand' as const },
    ]
  })
  const deliveryPointStats = computed(() => {
    const enabledCount = deliveryPoints.value.filter((item) => item.enabled !== false).length
    return [
      { label: '配送点', value: String(deliveryPoints.value.length), sub: '固定无人机送达点', tone: 'brand' as const },
      { label: '启用点位', value: String(enabledCount), sub: '用户地址自动匹配', tone: 'success' as const },
      { label: '覆盖门店', value: String(new Set(deliveryPoints.value.map((item) => item.storeId).filter(Boolean)).size), sub: '已绑定门店数', tone: 'warning' as const },
      { label: '平均半径', value: `${averageServiceRadiusKm.value}km`, sub: '服务半径均值', tone: 'brand' as const },
    ]
  })
  const averageServiceRadiusKm = computed(() => {
    if (!deliveryPoints.value.length) return '0'
    const total = deliveryPoints.value.reduce((sum, item) => sum + (item.serviceRadiusKm ?? 0), 0)
    return (total / deliveryPoints.value.length).toFixed(1).replace(/\.0$/, '')
  })

  async function loadDeliveryRules(): Promise<void> {
    if (!options.isDeliveryRulesCenter.value && !options.isDeliveryMinAmountCenter.value) return
    loadingDeliveryRules.value = true
    try {
      deliveryRules.value = await fetchDeliveryRules()
    } catch (error) {
      ElMessage.error(error instanceof Error ? error.message : '配送规则加载失败')
    } finally {
      loadingDeliveryRules.value = false
    }
  }

  async function loadDeliveryAreas(): Promise<void> {
    if (!options.isDeliveryAreasCenter.value) return
    loadingDeliveryAreas.value = true
    try {
      deliveryAreas.value = await fetchDeliveryAreas()
    } catch (error) {
      ElMessage.error(error instanceof Error ? error.message : '配送范围加载失败')
    } finally {
      loadingDeliveryAreas.value = false
    }
  }

  async function loadDeliveryPoints(): Promise<void> {
    if (!options.isDeliveryPointsCenter.value) return
    loadingDeliveryPoints.value = true
    try {
      deliveryPoints.value = await fetchDeliveryPoints()
    } catch (error) {
      ElMessage.error(error instanceof Error ? error.message : '配送点加载失败')
    } finally {
      loadingDeliveryPoints.value = false
    }
  }

  async function openDeliveryRuleEditor(item?: DeliveryRule): Promise<void> {
    await options.ensureEditorOptions()
    Object.assign(deliveryRuleForm, {
      id: item?.id,
      storeId: item?.storeId || '',
      name: item?.name || '',
      minOrderAmount: fenAmountToYuan(item?.minOrderAmount) ?? 0,
      baseFee: fenAmountToYuan(item?.baseFee) ?? 0,
      freeThreshold: fenAmountToYuan(item?.freeThreshold) ?? 0,
      perKmFee: fenAmountToYuan(item?.perKmFee) ?? 0,
      startDistanceKm: item?.startDistanceKm ?? 0,
      maxDistanceKm: item?.maxDistanceKm ?? 0,
    })
    deliveryRuleStoreIds.value = item?.storeId ? item.storeId.split(',').map((id) => id.trim()).filter(Boolean) : []
    deliveryRuleEditorVisible.value = true
  }

  async function openDeliveryAreaEditor(): Promise<void> {
    await options.ensureEditorOptions()
    Object.assign(deliveryAreaForm, {
      storeId: '',
      type: 'RADIUS',
      radiusKm: 3,
      polygon: '',
      feeTier: 0,
      sort: 0,
    })
    deliveryAreaStoreIds.value = []
    deliveryAreaEditorVisible.value = true
  }

  async function openDeliveryPointEditor(item?: DeliveryPoint): Promise<void> {
    await options.ensureEditorOptions()
    Object.assign(deliveryPointForm, {
      id: item?.id,
      storeId: item?.storeId || '',
      name: item?.name || '',
      address: item?.address || '',
      longitude: item?.longitude ?? 0,
      latitude: item?.latitude ?? 0,
      contactName: item?.contactName || '',
      contactPhone: item?.contactPhone || '',
      serviceRadiusKm: item?.serviceRadiusKm ?? 1,
      enabled: item?.enabled ?? true,
      sort: item?.sort ?? 0,
      remark: item?.remark || '',
    })
    deliveryPointEditorVisible.value = true
  }

  function openDeliveryPointLocation(item: DeliveryPoint): void {
    selectedDeliveryPoint.value = item
    deliveryPointLocationVisible.value = true
  }

  async function submitDeliveryRule(): Promise<void> {
    const name = deliveryRuleForm.name.trim()
    if (!name) {
      ElMessage.warning('请填写规则名称')
      return
    }
    deliveryRuleSubmitting.value = true
    try {
      const payload: DeliveryRuleSavePayload = {
        id: deliveryRuleForm.id,
        storeId: deliveryRuleStoreIds.value.join(',') || undefined,
        name,
        minOrderAmount: yuanAmountToFen(deliveryRuleForm.minOrderAmount),
        baseFee: yuanAmountToFen(deliveryRuleForm.baseFee),
        freeThreshold: yuanAmountToFen(deliveryRuleForm.freeThreshold),
        perKmFee: yuanAmountToFen(deliveryRuleForm.perKmFee),
        startDistanceKm: deliveryRuleForm.startDistanceKm,
        maxDistanceKm: deliveryRuleForm.maxDistanceKm,
      }
      await updateDeliveryRule(payload)
      ElMessage.success(isEditingDeliveryRule.value ? '配送规则已更新' : '配送规则已创建')
      deliveryRuleEditorVisible.value = false
      await loadDeliveryRules()
    } catch (error) {
      ElMessage.error(error instanceof Error ? error.message : '配送规则保存失败')
    } finally {
      deliveryRuleSubmitting.value = false
    }
  }

  async function submitDeliveryArea(): Promise<void> {
    const storeId = deliveryAreaStoreIds.value.join(',')
    if (!storeId) {
      ElMessage.warning('请选择门店')
      return
    }
    if (deliveryAreaForm.type === 'POLYGON' && !deliveryAreaForm.polygon?.trim()) {
      ElMessage.warning('多边形范围需要填写坐标串')
      return
    }
    deliveryAreaSubmitting.value = true
    try {
      const payload: DeliveryAreaSavePayload = {
        storeId,
        type: deliveryAreaForm.type,
        radiusKm: deliveryAreaForm.type === 'RADIUS' ? deliveryAreaForm.radiusKm : undefined,
        polygon: deliveryAreaForm.type === 'POLYGON' ? deliveryAreaForm.polygon?.trim() || undefined : undefined,
        feeTier: yuanAmountToFen(deliveryAreaForm.feeTier),
        sort: deliveryAreaForm.sort,
      }
      await createDeliveryArea(payload)
      ElMessage.success('配送范围已创建')
      deliveryAreaEditorVisible.value = false
      await loadDeliveryAreas()
    } catch (error) {
      ElMessage.error(error instanceof Error ? error.message : '配送范围保存失败')
    } finally {
      deliveryAreaSubmitting.value = false
    }
  }

  async function submitDeliveryPoint(): Promise<void> {
    const name = deliveryPointForm.name.trim()
    const address = deliveryPointForm.address.trim()
    if (!name) {
      ElMessage.warning('请填写配送点名称')
      return
    }
    if (!address || !Number.isFinite(deliveryPointForm.longitude) || !Number.isFinite(deliveryPointForm.latitude)) {
      ElMessage.warning('请在地图上选择配送点位置')
      return
    }
    deliveryPointSubmitting.value = true
    try {
      await saveDeliveryPoint({
        id: deliveryPointForm.id,
        storeId: deliveryPointForm.storeId || undefined,
        name,
        address,
        longitude: deliveryPointForm.longitude,
        latitude: deliveryPointForm.latitude,
        contactName: deliveryPointForm.contactName?.trim() || undefined,
        contactPhone: deliveryPointForm.contactPhone?.trim() || undefined,
        serviceRadiusKm: deliveryPointForm.serviceRadiusKm,
        enabled: deliveryPointForm.enabled,
        sort: deliveryPointForm.sort,
        remark: deliveryPointForm.remark?.trim() || undefined,
      })
      ElMessage.success(deliveryPointForm.id ? '配送点已更新' : '配送点已创建')
      deliveryPointEditorVisible.value = false
      await loadDeliveryPoints()
    } catch (error) {
      ElMessage.error(error instanceof Error ? error.message : '配送点保存失败')
    } finally {
      deliveryPointSubmitting.value = false
    }
  }

  async function removeDeliveryArea(item: DeliveryArea): Promise<void> {
    try {
      await ElMessageBox.confirm(`确定删除${item.storeName || item.storeId}的配送范围吗？`, '删除配送范围', {
        type: 'warning',
        confirmButtonText: '确认删除',
        cancelButtonText: '取消',
      })
    } catch {
      return
    }
    deliveryAreaSubmitting.value = true
    try {
      await deleteDeliveryArea(item.id)
      ElMessage.success('配送范围已删除')
      await loadDeliveryAreas()
    } catch (error) {
      ElMessage.error(error instanceof Error ? error.message : '配送范围删除失败')
    } finally {
      deliveryAreaSubmitting.value = false
    }
  }

  async function removeDeliveryRule(item: DeliveryRule): Promise<void> {
    try {
      await ElMessageBox.confirm(`确定删除规则"${item.name}"吗？`, '删除配送规则', {
        type: 'warning',
        confirmButtonText: '确认删除',
        cancelButtonText: '取消',
      })
    } catch {
      return
    }
    deliveryRuleSubmitting.value = true
    try {
      await deleteDeliveryRule(item.id)
      ElMessage.success('配送规则已删除')
      await loadDeliveryRules()
    } catch (error) {
      ElMessage.error(error instanceof Error ? error.message : '配送规则删除失败')
    } finally {
      deliveryRuleSubmitting.value = false
    }
  }

  async function removeDeliveryPoint(item: DeliveryPoint): Promise<void> {
    try {
      await ElMessageBox.confirm(`确定删除配送点"${item.name}"吗？`, '删除配送点', {
        type: 'warning',
        confirmButtonText: '确认删除',
        cancelButtonText: '取消',
      })
    } catch {
      return
    }
    deliveryPointSubmitting.value = true
    try {
      await deleteDeliveryPoint(item.id)
      ElMessage.success('配送点已删除')
      await loadDeliveryPoints()
    } catch (error) {
      ElMessage.error(error instanceof Error ? error.message : '配送点删除失败')
    } finally {
      deliveryPointSubmitting.value = false
    }
  }

  return {
    loadingDeliveryRules,
    loadingDeliveryAreas,
    loadingDeliveryPoints,
    deliveryRuleEditorVisible,
    deliveryAreaEditorVisible,
    deliveryPointEditorVisible,
    deliveryPointLocationVisible,
    deliveryRuleSubmitting,
    deliveryAreaSubmitting,
    deliveryPointSubmitting,
    deliveryRules,
    deliveryAreas,
    deliveryPoints,
    selectedDeliveryPoint,
    deliveryRuleStoreIds,
    deliveryAreaStoreIds,
    deliveryRuleForm,
    deliveryAreaForm,
    deliveryPointForm,
    deliveryRuleStats,
    deliveryMinAmountStats,
    deliveryAreaStats,
    deliveryPointStats,
    loadDeliveryRules,
    loadDeliveryAreas,
    loadDeliveryPoints,
    openDeliveryRuleEditor,
    openDeliveryAreaEditor,
    openDeliveryPointEditor,
    openDeliveryPointLocation,
    submitDeliveryRule,
    submitDeliveryArea,
    submitDeliveryPoint,
    removeDeliveryArea,
    removeDeliveryRule,
    removeDeliveryPoint,
  }
}
